"use client";

import type { InputProps } from "@/components/ui/input";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  NON_NEGATIVE_RANGE,
  describeNumberRange,
  parseNumberInRange,
  type NumberRange,
} from "@shared/validation/number-in-range";

type ControlSize = NonNullable<InputProps["size"]>;

interface CalculatorNumberFieldProps {
  id: string;
  label: string;
  value: string;
  description?: string;
  size?: ControlSize;
  range?: NumberRange;
  step?: number | "any";
  prefix?: string;
  suffix?: string;
  allowBlank?: boolean;
  required?: boolean;
  onChange: (value: string) => void;
}

function UnitAddon({ id, unit, align }: { id: string; unit: string | undefined; align: "inline-start" | "inline-end" }) {
  if (unit === undefined) return null;
  return (
    <InputGroupAddon id={id} align={align}>
      {unit}
    </InputGroupAddon>
  );
}

export function CalculatorNumberField({
  id,
  label,
  value,
  description,
  size = "default",
  range = NON_NEGATIVE_RANGE,
  step,
  prefix,
  suffix,
  allowBlank = false,
  required = false,
  onChange,
}: CalculatorNumberFieldProps) {
  const isBlankAllowed = allowBlank && value.trim() === "";
  const isInvalid = !isBlankAllowed && parseNumberInRange(value, range) === null;
  const isWholeNumber = range.wholeNumber === true;
  const ids = partIds(id);
  return (
    <Field data-invalid={isInvalid}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <InputGroup size={size}>
        <UnitAddon id={ids.prefix} unit={prefix} align="inline-start" />
        <InputGroupInput
          id={id}
          type="number"
          inputMode={isWholeNumber ? "numeric" : "decimal"}
          min={range.min}
          max={range.max}
          step={step ?? (isWholeNumber ? 1 : "any")}
          variant="mono"
          value={value}
          required={required}
          aria-invalid={isInvalid}
          aria-describedby={describedBy(ids, { prefix, suffix, description, isInvalid })}
          onChange={(event) => onChange(event.target.value)}
        />
        <UnitAddon id={ids.suffix} unit={suffix} align="inline-end" />
      </InputGroup>
      {description === undefined ? null : (
        <FieldDescription id={ids.description}>{description}</FieldDescription>
      )}
      {isInvalid ? <FieldError id={ids.error}>{describeNumberRange(range)}</FieldError> : null}
    </Field>
  );
}

function partIds(id: string) {
  return {
    prefix: `${id}-prefix`,
    suffix: `${id}-suffix`,
    description: `${id}-description`,
    error: `${id}-error`,
  };
}

function describedBy(
  ids: ReturnType<typeof partIds>,
  parts: { prefix?: string; suffix?: string; description?: string; isInvalid: boolean },
): string | undefined {
  const present = [
    parts.prefix === undefined ? null : ids.prefix,
    parts.suffix === undefined ? null : ids.suffix,
    parts.description === undefined ? null : ids.description,
    parts.isInvalid ? ids.error : null,
  ].filter((part) => part !== null);
  return present.length > 0 ? present.join(" ") : undefined;
}
